import { IAPMIMETYPE } from 'bff';
import { displayAsyncToast } from '@sanlam-glacier/app-common/modules/toasts';
import { base64ToBlob, downloadBlob } from '@sanlam-glacier/app-common/utils';

type BackgroundDownloadWording = {
  message: string;
  title: string;
  successMessage: string;
  errorMessage: string;
  promiseCompleteTitle: string;
};

type DownloadResponse = {
  base64: string;
  mimeType: IAPMIMETYPE | string;
  fileName: string;
};

type DownloadDocumentBackgroundProps = {
  downloadFn: () => Promise<DownloadResponse | undefined>;
  wording?: BackgroundDownloadWording;
};

var GENERIC_WORDING: BackgroundDownloadWording = {
  message: 'The file will be available in your downloads folder when complete.',
  title: 'File downloading...',
  successMessage: 'PDF file downloaded, check your downloads folder.',
  errorMessage: 'Failed to download',
  promiseCompleteTitle: 'PDF file download'
};

export const downloadDocumentBackground = ({
  downloadFn,
  wording
}: DownloadDocumentBackgroundProps) => {
  const wordingToUse = wording ?? GENERIC_WORDING;

  const downloadPDF = async (): Promise<void> => {
    const result = await downloadFn();
    if (!result) {
      throw new Error('No file returned');
    }

    const { base64, fileName, mimeType } = result;
    const blob = base64ToBlob(base64, mimeType);

    await downloadBlob(blob, fileName);
  };

  displayAsyncToast({
    ...wordingToUse,
    promise: downloadPDF,
    toastId: `signing:application-download:${Date.now()}`
  });
};

type DownloadFromBlobBackgroundProps = {
  downloadFn: () => Promise<DownloadFromBlobResponse | undefined>;
  wording?: BackgroundDownloadWording;
  toastId: string;
};

type DownloadFromBlobResponse = {
  blob: Blob;
  fileName: string;
};

export const downloadDocumentBackgroundFromBlob = ({
  downloadFn,
  wording,
  toastId
}: DownloadFromBlobBackgroundProps) => {
  const wordingToUse = wording ?? GENERIC_WORDING;

  const downloadFile = async (): Promise<void> => {
    const result = await downloadFn();
    if (!result) {
      throw new Error('No file returned');
    }

    const { blob, fileName } = result;
    await downloadBlob(blob, fileName);
  };

  displayAsyncToast({
    ...wordingToUse,
    promise: downloadFile,
    toastId: toastId
  });
};
